import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';
import Icon from '../../components/AppIcon';
import AuditTimelineEntry from './components/AuditTimelineEntry';
import AuditFilterControls from './components/AuditFilterControls';
import AuditStatsCard from './components/AuditStatsCard';
import ActivityHeatMap from './components/ActivityHeatMap';
import auditService from '../../services/auditService';
import auditRealtimeService from '../../services/auditRealtimeService';
import notificationService from '../../services/notificationService';
import { logger } from '../../utils/logger';
import { format, subDays } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { hasFeatureAccess, OFFICER_FEATURES } from '../../utils/rolePermissions';





const AuditTrailDashboard = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditStats, setAuditStats] = useState(null);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [realtimeEnabled, setRealtimeEnabled] = useState(true);
  const [realtimeStatus, setRealtimeStatus] = useState('disconnected');
  const [notifications, setNotifications] = useState([]);
  
  // Filter states
  const [filters, setFilters] = useState({
    actionType: 'all',
    resourceType: 'all',
    dateRange: {
      start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
      end: format(new Date(), 'yyyy-MM-dd')
    }
  });

  useEffect(() => {
    loadAuditData();
  }, [filters]);

  // Check officer access
  const hasAccess = hasFeatureAccess(userProfile, OFFICER_FEATURES?.AUDIT_TRAIL);

  // Redirect non-officers
  useEffect(() => {
    if (!loading && userProfile && !hasAccess) {
      navigate('/settings-and-privacy');
    }
  }, [hasAccess, loading, userProfile, navigate]);

  // Real-time subscription effect
  useEffect(() => {
    if (!realtimeEnabled) return;

    try {
      setRealtimeStatus('connecting');

      // Subscribe to audit log changes
      const channel = auditRealtimeService?.subscribe(
        (payload) => {
          handleRealtimeEvent(payload);
        },
        {
          // Apply filters to subscription if needed
          actionType: filters?.actionType !== 'all' ? filters?.actionType : null,
          resourceType: filters?.resourceType !== 'all' ? filters?.resourceType : null
        }
      );

      setRealtimeStatus('connected');

      // Cleanup subscription on unmount
      return () => {
        auditRealtimeService?.unsubscribe();
        setRealtimeStatus('disconnected');
      };
    } catch (error) {
      logger?.error('Failed to setup real-time subscription', error);
      setRealtimeStatus('error');
    }
  }, [realtimeEnabled, filters?.actionType, filters?.resourceType]);

  const handleRealtimeEvent = (payload) => {
    const { eventType, record } = payload;

    // Show notification
    const notification = notificationService?.showAuditNotification(payload);
    setNotifications(prev => [notification, ...prev]?.slice(0, 10));

    // Update audit logs list
    if (eventType === 'INSERT') {
      // Add new log to the beginning
      setAuditLogs(prev => [record, ...prev]);

      // Update stats
      setAuditStats(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          totalEvents: (prev?.totalEvents || 0) + 1,
          byActionType: {
            ...prev?.byActionType,
            [record?.actionType]: (prev?.byActionType?.[record?.actionType] || 0) + 1
          },
          byResourceType: {
            ...prev?.byResourceType,
            [record?.resourceType]: (prev?.byResourceType?.[record?.resourceType] || 0) + 1
          }
        };
      });
    }
  };

  const loadAuditData = async () => {
    setLoading(true);
    setError('');

    try {
      // Load audit logs
      const { data: logs, error: logsError } = await auditService?.getAuditLogs({
        actionType: filters?.actionType,
        resourceType: filters?.resourceType,
        dateRange: filters?.dateRange,
        limit: 100
      });

      if (logsError) {
        setError(logsError?.message || 'Failed to load audit logs');
        logger?.error('Failed to load audit logs', logsError);
      } else {
        setAuditLogs(logs || []);
      }

      // Load audit statistics
      const { data: stats, error: statsError } = await auditService?.getAuditStats({
        dateRange: filters?.dateRange
      });

      if (statsError) {
        logger?.error('Failed to load audit stats', statsError);
      } else {
        setAuditStats(stats);
      }
    } catch (err) {
      setError('An unexpected error occurred');
      logger?.error('Audit dashboard load error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleExport = async () => {
    const { data, error } = await auditService?.exportAuditLogs(filters);
    
    if (error) {
      setError('Failed to export audit logs');
      logger?.error('Export failed', error);
      return;
    }
    
    // Trigger download
    const link = document?.createElement('a');
    link.href = data?.url;
    link.download = data?.filename;
    document?.body?.appendChild(link);
    link?.click();
    document?.body?.removeChild(link);
    URL?.revokeObjectURL(data?.url);
  };

  const toggleRealtime = () => {
    setRealtimeEnabled(prev => !prev);
  };

  const clearNotifications = () => {
    setNotifications([]);
    notificationService?.clearStoredNotifications();
  };

  const filteredLogs = auditLogs?.filter(log => {
    if (!searchQuery) return true;
    const query = searchQuery?.toLowerCase();
    return (
      log?.user?.email?.toLowerCase()?.includes(query) ||
      log?.actionType?.toLowerCase()?.includes(query) ||
      log?.resourceType?.toLowerCase()?.includes(query)
    );
  });

  const getTotalModifications = () => auditStats?.totalEvents || 0;
  const getCreateCount = () => auditStats?.byActionType?.CREATE || 0;
  const getUpdateCount = () => auditStats?.byActionType?.UPDATE || 0;
  const getDeleteCount = () => auditStats?.byActionType?.DELETE || 0;

  const getRealtimeStatusColor = () => {
    switch (realtimeStatus) {
      case 'connected': return 'text-success';
      case 'connecting': return 'text-warning';
      case 'error': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getRealtimeStatusText = () => {
    switch (realtimeStatus) {
      case 'connected': return 'Live';
      case 'connecting': return 'Connecting...';
      case 'error': return 'Error';
      default: return 'Disconnected';
    }
  };

  return (
    <>
      <Helmet>
        <title>Audit Trail Dashboard - MyBodyToday</title>
        <meta name="description" content="Comprehensive modification history tracking and integrity verification for symptom data" />
      </Helmet>

      <div className="min-h-screen bg-background pb-24">
        {/* Header */}
        <div className="bg-gradient-to-br from-primary to-primary-dark text-white px-4 sm:px-6 py-6 sm:py-8">
          <div className="max-w-7xl mx-auto">
            <button
              onClick={() => navigate(-1)}
              className="mb-4 flex items-center gap-2 text-white/80 hover:text-white transition-colors"
              aria-label="Go back"
            >
              <Icon name="ArrowLeft" size={20} strokeWidth={2} />
              <span className="text-sm font-medium">Back</span>
            </button>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                  <Icon name="Shield" size={24} strokeWidth={2} />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-heading font-bold">Audit Trail Dashboard</h1>
                  <p className="text-white/80 text-sm mt-1">Modification history & integrity verification</p>
                </div>
              </div>

              {/* Real-time Status Indicator */}
              <div className="flex items-center gap-3">
                <button
                  onClick={toggleRealtime}
                  className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                  aria-label="Toggle real-time updates"
                >
                  <div className={`w-2 h-2 rounded-full ${realtimeEnabled && realtimeStatus === 'connected' ? 'bg-success animate-pulse' : 'bg-muted-foreground'}`}></div>
                  <span className="text-sm font-medium">
                    {realtimeEnabled ? getRealtimeStatusText() : 'Paused'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
          {/* Real-time Notifications */}
          {notifications?.length > 0 && (
            <div className="bg-card rounded-xl border border-border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name="Bell" size={20} className="text-primary" strokeWidth={2} />
                  <h3 className="text-sm font-semibold text-foreground">Recent Activity</h3>
                  <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
                    {notifications?.length}
                  </span>
                </div>
                <button
                  onClick={clearNotifications}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Clear all
                </button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {notifications?.map((notif) => (
                  <div
                    key={notif?.id}
                    className="flex items-start gap-3 p-3 bg-background rounded-lg border border-border"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      notif?.type === 'success' ? 'bg-success/10 text-success' :
                      notif?.type === 'warning' ? 'bg-warning/10 text-warning' :
                      notif?.type === 'error'? 'bg-error/10 text-error' : 'bg-primary/10 text-primary'
                    }`}>
                      <Icon name="Activity" size={16} strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{notif?.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{notif?.message}</p>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {format(new Date(notif?.timestamp), 'HH:mm')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Statistics Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <AuditStatsCard
              title="Total Events"
              value={getTotalModifications()}
              icon="Activity"
              color="primary"
            />
            <AuditStatsCard
              title="Created"
              value={getCreateCount()}
              icon="Plus"
              color="success"
            />
            <AuditStatsCard
              title="Updated"
              value={getUpdateCount()}
              icon="Edit"
              color="warning"
            />
            <AuditStatsCard
              title="Deleted"
              value={getDeleteCount()}
              icon="Trash2"
              color="error"
            />
          </div>

          {/* Activity Heat Map */}
          {auditStats && (
            <ActivityHeatMap data={auditLogs} />
          )}

          {/* Filter Controls */}
          <AuditFilterControls
            filters={filters}
            onFilterChange={handleFilterChange}
            onExport={handleExport}
          />

          {/* Search Bar */}
          <div className="bg-card rounded-xl border border-border p-4">
            <div className="relative">
              <Icon
                name="Search"
                size={20}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                strokeWidth={2}
              />
              <input
                type="text"
                placeholder="Search by user, action, or resource type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-10 pr-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-error/10 border border-error/20 rounded-xl p-4 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" strokeWidth={2} />
              <div>
                <h3 className="text-sm font-medium text-error mb-1">Error Loading Audit Data</h3>
                <p className="text-sm text-error/80">{error}</p>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          )}

          {/* Audit Timeline */}
          {!loading && filteredLogs?.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-heading font-semibold text-foreground">
                  Modification History ({filteredLogs?.length})
                </h2>
              </div>
              
              <div className="space-y-3">
                {filteredLogs?.map((log) => (
                  <AuditTimelineEntry key={log?.id} log={log} />
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {!loading && filteredLogs?.length === 0 && (
            <div className="bg-card rounded-xl border border-border p-8 sm:p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Icon name="FileSearch" size={32} className="text-muted-foreground" strokeWidth={2} />
              </div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                No Audit Logs Found
              </h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                {searchQuery
                  ? 'No logs match your search criteria. Try adjusting your filters or search query.' :'No modification history available for the selected date range.'}
              </p>
            </div>
          )}
        </div>

        <BottomTabNavigation />
      </div>
    </>
  );
};

export default AuditTrailDashboard;